import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { validateToken, uploadDocuments } from "../../api/apiService";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import UploadIcon from "@mui/icons-material/Upload";

const UploadDocuments = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [solicitante, setSolicitante] = useState("");
  const [documentosNecessarios, setDocumentosNecessarios] = useState([]);
  const [files, setFiles] = useState({});
  const [expiraEm, setExpiraEm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmPartialUpload, setConfirmPartialUpload] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const inputRefs = useRef({});

  const handleValidateToken = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await validateToken(token);
      setSolicitante(data.solicitante);
      setDocumentosNecessarios(data.documentosNecessarios);
      setExpiraEm(data.expiraEm);
    } catch (error) {
      console.error("Erro ao validar o token:", error);
      setSnackbarMessage("Erro ao validar o token.");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      handleValidateToken();
    }
  }, [token, handleValidateToken]);

  const handleFileChange = (event, tipoSolicitacao, tipoDocumento) => {
    const selectedFile = event.target.files[0];
    if (selectedFile && !selectedFile.type.startsWith("image/")) {
      setSnackbarMessage("Por favor, selecione apenas arquivos de imagem.");
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [tipoSolicitacao]: {
        ...prev[tipoSolicitacao],
        [tipoDocumento]: selectedFile,
      },
    }));
  };

  const handleUpload = async () => {
    const allFiles = [];
    const allTipos = [];

    documentosNecessarios.forEach((doc) => {
      doc.tiposDocumentosNecessarios.forEach((tipo) => {
        const file = files[doc.tipoSolicitacao]?.[tipo];
        if (file) {
          allFiles.push(file);
          allTipos.push(tipo);
        }
      });
    });

    const totalRequired = documentosNecessarios.reduce(
      (acc, doc) => acc + doc.tiposDocumentosNecessarios.length,
      0
    );

    if (allFiles.length < totalRequired && !confirmPartialUpload) {
      // Abre o dialog de confirmação se documentos estão faltando
      setShowConfirmationDialog(true);
      return;
    }

    try {
      if (allFiles.length === 0) {
        setSnackbarMessage("Nenhum arquivo selecionado para envio.");
        return;
      }

      setIsUploading(true);

      await uploadDocuments(token, allTipos, allFiles);
      setShowModal(true);
    } catch (error) {
      console.error("Erro ao enviar documentos:", error);
      setSnackbarMessage("Erro ao enviar os documentos. Tente novamente.");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    navigate("/");
  };

  const handleCloseConfirmationDialog = () => {
    setShowConfirmationDialog(false);
    setConfirmPartialUpload(false);
  };

  const handleConfirmUpload = () => {
    setShowConfirmationDialog(false);
    setConfirmPartialUpload(true);
    handleUpload(); // Chama novamente para enviar os documentos
  };

  const handleCloseSnackbar = () => setSnackbarMessage("");

  return (
    <Box sx={{ padding: "2rem" }}>
      <Typography variant="h4" textAlign="center" mb={3}>
        Upload de Documentos
      </Typography>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : solicitante ? (
        <Box>
          <Typography variant="h5" mb={1}>
            Solicitante: {solicitante}
          </Typography>
          <Typography variant="subtitle1" mb={3}>
            Token válido até: {new Date(expiraEm).toLocaleString()}
          </Typography>
          <Typography variant="h6" mb={2}>
            Documentos Necessários
          </Typography>
          {documentosNecessarios.map((doc) => (
            <Box key={doc.tipoSolicitacao} mb={3}>
              <Typography variant="subtitle1" gutterBottom>
                {doc.tipoSolicitacao}
              </Typography>
              <List>
                {doc.tiposDocumentosNecessarios.map((tipo) => {
                  const refKey = `${doc.tipoSolicitacao}-${tipo}`;
                  if (!inputRefs.current[refKey]) {
                    inputRefs.current[refKey] = React.createRef();
                  }

                  const selectedFile =
                    files[doc.tipoSolicitacao]?.[tipo]?.name ||
                    "Nenhum arquivo selecionado";

                  return (
                    <ListItem
                      key={refKey}
                      divider
                      onClick={() => inputRefs.current[refKey].current.click()}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: files[doc.tipoSolicitacao]?.[tipo]
                          ? "rgba(0, 200, 83, 0.1)"
                          : "transparent",
                        "&:hover": {
                          backgroundColor: "rgba(0, 123, 255, 0.1)",
                        },
                      }}
                    >
                      <ListItemText
                        primary={tipo}
                        secondary={
                          <Typography
                            variant="caption"
                            sx={{
                              color: files[doc.tipoSolicitacao]?.[tipo]
                                ? "green"
                                : "text.secondary",
                            }}
                          >
                            {selectedFile}
                          </Typography>
                        }
                      />
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          inputRefs.current[refKey].current.click();
                        }}
                      >
                        <UploadIcon
                          color={
                            files[doc.tipoSolicitacao]?.[tipo]
                              ? "success"
                              : "primary"
                          }
                        />
                      </IconButton>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        ref={inputRefs.current[refKey]}
                        onChange={(e) =>
                          handleFileChange(e, doc.tipoSolicitacao, tipo)
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUpload}
            sx={{ mt: 3 }}
            disabled={isUploading} // 🔹 Desabilita o botão enquanto estiver enviando
          >
            {isUploading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              "Enviar Documentos"
            )}
          </Button>
          ;
        </Box>
      ) : (
        <Typography variant="h6" color="error" textAlign="center">
          Token inválido, expirado ou já utilizado.
        </Typography>
      )}

      {/* Dialog de confirmação */}
      <Dialog
        open={showConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#ff9800", // Laranja para chamar atenção
            color: "white",
            fontWeight: "bold",
            textAlign: "center",
          }}
        >
          ⚠️ Confirmação de Envio ⚠️
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#fff3e0", // Fundo em tom claro
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#d84315", // Vermelho escuro para destaque
              fontWeight: "bold",
              marginBottom: "15px",
            }}
          >
            Atenção! Alguns documentos estão faltando.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#d84315",
              fontWeight: "500",
              marginBottom: "20px",
            }}
          >
            Você deseja continuar com o envio apenas dos documentos
            selecionados? Esta ação não poderá ser desfeita!
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmPartialUpload}
                onChange={(e) => setConfirmPartialUpload(e.target.checked)}
                sx={{
                  color: "#ff9800",
                  "&.Mui-checked": { color: "#ff9800" },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  color: "#d84315",
                }}
              >
                Confirmo que desejo enviar apenas os documentos selecionados.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "center",
            padding: "20px",
            backgroundColor: "#fff3e0",
          }}
        >
          <Button
            onClick={handleCloseConfirmationDialog}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            disabled={!confirmPartialUpload}
            sx={{
              backgroundColor: confirmPartialUpload ? "#4caf50" : "#c8e6c9",
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: confirmPartialUpload ? "#388e3c" : "#c8e6c9",
              },
            }}
          >
            Confirmar e Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showModal} onClose={handleCloseModal}>
        <DialogTitle>Documentos enviados!</DialogTitle>
        <DialogContent>
          <Typography>Aguarde novas atualizações.</Typography>
          <Typography>
            Responda a mensagem do WhatsApp caso necessário.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default UploadDocuments;
